import { CssCustomProperties } from '@wix/component-protocol/schema';
import { CssCustomPropertyGroupStyleItem } from '../../../types';

export type DuplicateBasedGroup = CssCustomPropertyGroupStyleItem;

/**
 * Alternative grouping strategy for CSS custom properties.
 *
 * Behavior:
 * - If NO duplicated cssPropertyTypes exist → Returns single group with all properties
 * - If duplicated cssPropertyTypes exist → Splits each property into its own separate group
 *
 * This differs from siblingGrouping which groups by sibling relationships defined in PROPERTY_SIBLINGS_MAP.
 *
 * @param properties - CSS custom properties to group
 * @returns Array of groups - single group if no duplicates, or one group per property if duplicates exist
 *
 * @example
 * // No duplicates - single group
 * groupByDuplication({
 *   prop1: { cssPropertyType: 'color' },
 *   prop2: { cssPropertyType: 'fontSize' }
 * })
 * // Returns: [[{styleItemId: 'prop1', ...}, {styleItemId: 'prop2', ...}]]
 *
 * @example
 * // Has duplicates - each property gets its own group
 * groupByDuplication({
 *   prop1: { cssPropertyType: 'color' },
 *   prop2: { cssPropertyType: 'color' },
 *   prop3: { cssPropertyType: 'fontSize' }
 * })
 * // Returns: [[{prop1}], [{prop2}], [{prop3}]]
 */
export const groupByDuplication = (
  properties: CssCustomProperties,
): DuplicateBasedGroup[][] => {
  const propertyEntries = Object.entries(properties);

  // Handle empty object
  if (propertyEntries.length === 0) {
    return [];
  }

  // Check if there are any duplicated cssPropertyTypes
  const cssPropertyTypeCounts = new Map<string | undefined, number>();
  propertyEntries.forEach(([_, item]) => {
    const type = item.cssPropertyType;
    cssPropertyTypeCounts.set(type, (cssPropertyTypeCounts.get(type) || 0) + 1);
  });

  const hasDuplicates = Array.from(cssPropertyTypeCounts.values()).some(
    (count) => count > 1,
  );

  // If no duplicates, return single group with all properties
  if (!hasDuplicates) {
    return [
      propertyEntries.map(([styleItemId, styleItem]) => ({
        styleItemId,
        styleItem,
      })),
    ];
  }

  // Has duplicates - split each property into its own group
  return propertyEntries.map(([styleItemId, styleItem]) => [
    { styleItemId, styleItem },
  ]);
};
